/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  createAsyncThunk,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  ShowflixCarousel,
  ShowflixCarousels,
  ShowflixScreensResponse,
  TShowflixCarouselScreenGetParams,
  TShowflixCarouselScreensNavGetParams
} from "@tivocorp/data-domain-lib";

import { DiagnosticLogger, Log, OperationalLogger } from "../../model/logging";
import { DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS } from "../../config";
import { DiagnosticSeverity } from "../../model/types";
import { PostSigninInteractor } from "../../model/interactor/postSigninInteractor";
import { ApplicationFeatureArea } from "../../model/enums";
import {
  GridDirection,
  GridEdgeBehaviour,
  moveCarouselRowDown,
  moveCarouselRowUp,
  moveCarouselTileLeft,
  moveCarouselTileRight,
  moveGridFocus
} from "../../model/navigation";
import type { TCarouselItem } from "../../model/types";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";
import { getImageHostBaseUrl } from "../utility";
import { extractServiceErrorMessage } from "../../model/utility/errorMessage";
import { SEE_MORE_RAIL_TILE_CAP } from "../utility/carouselPagination";
import {
  fetchShowflixCarouselTilePage,
  mapShowflixCarouselItemsToAppData
} from "../utility/showflixCarouselUtility";
import {
  hasGenreSubFilters,
  mapServerShowflixFilters,
  mapServerShowflixGenres
} from "./showsAndMoviesFilters";
import type {
  TShowsAndMoviesFilterMeta,
  TShowsAndMoviesGenreMeta
} from "./showsAndMovies.types";
import {
  SHOWS_AND_MOVIES_API_ENDPOINT,
  SHOWS_AND_MOVIES_SCREEN_ID
} from "./showsAndMoviesConstants";
import {
  ShowsAndMoviesFilterId,
  ShowsAndMoviesFocusRegion,
  ShowsAndMoviesGenreId
} from "./showsAndMovies.types";
import { getShowsAndMoviesFeatureArea } from "./showsAndMoviesFilters";
import { ShowsAndMoviesScreenTemplateType } from "./showsAndMovies.types";
import type {
  ShowsAndMoviesState,
  TShowsAndMoviesCarouselData
} from "./showsAndMovies.types";

const TAG: string = "showsAndMoviesSlice";
const SINGLE_CAROUSEL_GRID_COLUMNS: number = 6;
/** Items requested per Showflix carousel offset page (rows and grid alike). */
const CAROUSEL_ITEM_PAGE_SIZE: number = 10;
const NAVIGATION_LOAD_FAILURE_MESSAGE: string =
  "Failed to load Shows & Movies screen navigation";
const CAROUSELS_LOAD_FAILURE_MESSAGE: string =
  "Failed to load Shows & Movies carousels";
const SHOWS_AND_MOVIES_SCREENS_API_ENDPOINT: string =
  "/v4/showsAndMovies/screens";

const ShowsAndMoviesOperationalLogger = OperationalLogger.create({
  screenId: SHOWS_AND_MOVIES_SCREEN_ID,
  apiEndpoint: SHOWS_AND_MOVIES_API_ENDPOINT
});

const DIAGNOSTIC_EVENT: string = "shows_and_movies_screen";

const logShowsAndMoviesDiagnostic = (
  message: string,
  severity: DiagnosticSeverity,
  details: Record<string, string>
): void => {
  DiagnosticLogger.log(DIAGNOSTIC_EVENT, message, severity, {
    screen_id: SHOWS_AND_MOVIES_SCREEN_ID,
    ...details
  });
};

const logShowsAndMoviesApiRequest = (
  apiName: string,
  status: "SUCCESS" | "FAIL" | "TIMEOUT",
  startedAt: number,
  error?: unknown
): void => {
  ShowsAndMoviesOperationalLogger.logEvent("apiRequest", {
    api_name: apiName,
    screen_id: SHOWS_AND_MOVIES_SCREEN_ID,
    status,
    latency_ms: String(Date.now() - startedAt),
    ...(error === undefined
      ? {}
      : {
          error_message: OperationalLogger.describeErrorMessage(error)
        })
  });
};

interface TShowsAndMoviesScreenPage {
  items: TShowsAndMoviesCarouselData[];
  pageCursor: string | null;
  refreshIntervalSecs?: number;
  catalogueKey?: string;
}

interface TShowsAndMoviesCarouselPage {
  carouselId: string;
  items: TCarouselItem[];
  /** Undefined when the server response omitted a total for this page. */
  resolvedCount: number | undefined;
  /** Items requested for this page — lets the reducer detect a short final page. */
  requestedCount: number;
}

/** Fulfilled/rejected action shape carrying the requestId stale-response guards read. */
type TGuardedAction<P> = PayloadAction<P, string, { requestId: string }>;

// ── Internal helpers ─────────────────────────────────────────────────────────

/** Clamps `index` to [0, length - 1]; returns 0 when `length` is 0 or less. */
const clampIndex = (index: number, length: number): number => {
  if (length <= 0) {
    return 0;
  }
  if (index < 0) {
    return 0;
  }
  if (index >= length) {
    return length - 1;
  }
  return index;
};

/**
 * Resets the carousel cursor and its synced bounds.
 *
 * Called whenever the tab or the genre changes: the rails on screen are about
 * to be replaced, so a cursor pointing into the old ones is meaningless and
 * would otherwise survive the swap.
 */
const resetCarouselFocus = (state: ShowsAndMoviesState): void => {
  state.focusedCarouselIndex = 0;
  state.focusedItemIndex = 0;
  state.carouselItemCounts = [];
  state.carouselScrollPositions = [];
};

/**
 * Clears the outgoing catalogue before a different filter's fetch begins.
 *
 * Without this, the old rails remain mounted for one render after their cursor
 * resets to zero, producing a visible snap before the replacement request
 * reaches its pending reducer.
 */
const clearCarouselsForFilterChange = (state: ShowsAndMoviesState): void => {
  state.carousels.items = [];
  state.carousels.pageCursor = null;
  state.carousels.loadingCarouselIds = [];
  state.carousels.requestId = null;
  state.carousels.error = "";
  state.carousels.isLoading = true;
  resetCarouselFocus(state);
};

const getSelectedScreenTemplateType = (
  state: ShowsAndMoviesState
): TShowsAndMoviesFilterMeta["templateType"] => {
  const selectedFilter = state.filterOptions.find(
    (option: TShowsAndMoviesFilterMeta): boolean =>
      option.id === state.selectedFilter
  );
  return selectedFilter === undefined ? undefined : selectedFilter.templateType;
};

const isSingleCarouselGrid = (state: ShowsAndMoviesState): boolean =>
  getSelectedScreenTemplateType(state) ===
  ShowsAndMoviesScreenTemplateType.SINGLE_CAROUSEL_GRID;

const toggleGenreSelection = (
  selectedGenres: ShowsAndMoviesGenreId[],
  genreId: ShowsAndMoviesGenreId
): ShowsAndMoviesGenreId[] => {
  if (genreId === ShowsAndMoviesGenreId.ALL) {
    return [ShowsAndMoviesGenreId.ALL];
  }

  const specificGenres = selectedGenres.filter(
    (selectedGenre: ShowsAndMoviesGenreId): boolean =>
      selectedGenre !== ShowsAndMoviesGenreId.ALL
  );
  if (specificGenres.includes(genreId)) {
    const remainingGenres = specificGenres.filter(
      (selectedGenre: ShowsAndMoviesGenreId): boolean =>
        selectedGenre !== genreId
    );
    return remainingGenres.length === 0
      ? [ShowsAndMoviesGenreId.ALL]
      : remainingGenres;
  }
  return [...specificGenres, genreId];
};

/**
 * Moves focus out of the strips and onto the first carousel row, restoring that
 * row's remembered tile. A no-op while the rails are still loading — there is
 * nothing to focus.
 */
const moveToFirstCarousel = (state: ShowsAndMoviesState): void => {
  if (state.carouselItemCounts.length === 0) {
    return;
  }
  state.focusRegion = ShowsAndMoviesFocusRegion.CAROUSEL_ITEMS;
  state.focusedCarouselIndex = 0;
  const firstSaved: number | undefined = state.carouselScrollPositions[0];
  state.focusedItemIndex = clampIndex(
    firstSaved !== undefined ? firstSaved : 0,
    state.carouselItemCounts[0]
  );
};

/**
 * Returns focus to the tab strip, highlighting the tab that is actually active
 * rather than whichever one was highlighted last — pressing UP out of the
 * content should land on the tab the user is looking at.
 */
const returnFocusToFilters = (state: ShowsAndMoviesState): void => {
  state.focusRegion = ShowsAndMoviesFocusRegion.FILTERS;
  const selectedIndex: number = state.filterOptions.findIndex(
    (option: TShowsAndMoviesFilterMeta): boolean =>
      option.id === state.selectedFilter
  );
  state.focusedFilterIndex = Math.max(0, selectedIndex);
};

/** Remembers the active rail slot before focus leaves the carousel region. */
const saveFocusedCarouselPosition = (state: ShowsAndMoviesState): void => {
  state.carouselScrollPositions[state.focusedCarouselIndex] =
    state.focusedItemIndex;
};

/**
 * The region directly above the carousels: the genre chips when the active tab
 * has them, the tab strip otherwise.
 */
const enterRegionAboveCarousels = (state: ShowsAndMoviesState): void => {
  if (hasGenreSubFilters(state.selectedFilter)) {
    state.focusRegion = ShowsAndMoviesFocusRegion.GENRES;
    const selectedIndex: number = state.genreOptions.findIndex(
      (option: TShowsAndMoviesGenreMeta): boolean =>
        state.selectedGenres.includes(option.id)
    );
    state.focusedGenreIndex = Math.max(0, selectedIndex);
    return;
  }
  returnFocusToFilters(state);
};

// ── Async thunks ──────────────────────────────────────────────────────────────

/**
 * Reject value for a request cancelled on purpose — leaving the screen runs
 * `NetworkGuard.cancelAllForNavigation()`, which aborts the request's own
 * controller and surfaces the transport's cancellation error. The rejected
 * reducers store it as-is, and an empty error raises no dialog.
 */
const REQUEST_CANCELLED: string = "";

/**
 * True when a rejection is a cancellation rather than a failure: the transport
 * reports one — axios/Sandwich (`CanceledError` / `ERR_CANCELED`) or DOM/fetch
 * (`AbortError`) — or the thunk's own signal was aborted.
 *
 * Reported as a failure, a BACK pressed before the screen loads raised the
 * navigation error dialog on the way out, while the screen was still mounted.
 *
 * @param error Rejection value from a Shows & Movies request.
 * @param signal The thunk's abort signal.
 * @returns True when the rejection represents a cancellation.
 */
const isRequestCancellation = (
  error: unknown,
  signal: AbortSignal
): boolean => {
  if (signal.aborted) {
    return true;
  }
  if (error === null || error === undefined) {
    return false;
  }
  const candidate = error as { name?: unknown; code?: unknown };
  return (
    candidate.name === "CanceledError" ||
    candidate.name === "AbortError" ||
    candidate.code === "ERR_CANCELED"
  );
};

/**
 * Fetches the screen navigation (list of available screens and their metadata)
 * from the Showflix service and syncs the filter/genre metadata lists.
 *
 * Called once on mount to populate the filter and genre chips with server-driven
 * metadata. The thunk reads the service from the PostSigninInteractor and calls
 * `fetchScreensNav()` to get the real screen list and genre options.
 *
 * Syncs both the filter options (from screens) and the genre options (from the
 * first screen that supplies genres. Curated screens such as For You have an
 * empty genre list, while Shows and Movies supply the shared genre chips.
 */
export const loadShowsAndMoviesScreenNavigation = createAsyncThunk<
  Pick<ShowsAndMoviesState, "filterOptions" | "genreOptions">,
  void,
  { rejectValue: string }
>(
  "showsAndMovies/loadScreenNavigation",
  async (_: void, { rejectWithValue, signal }) => {
    const startedAt: number = Date.now();
    try {
      Log.d(
        TAG,
        "loadShowsAndMoviesScreenNavigation() — fetching from Showflix API"
      );
      const interactor = new PostSigninInteractor();
      const params: TShowflixCarouselScreensNavGetParams = {};
      const response: ShowflixScreensResponse =
        await interactor.fetchShowflixScreensNav(
          params,
          { signal },
          ApplicationFeatureArea.SHOWS_AND_MOVIES
        );
      Log.d(
        TAG,
        "loadShowsAndMoviesScreenNavigation() — received /screens response",
        response.screens
      );

      const filterOptions = mapServerShowflixFilters(response.screens);

      const genreScreen = response.screens.find(
        (screen): boolean =>
          Array.isArray(screen.genreList) && screen.genreList.length > 0
      );
      const genreOptions = mapServerShowflixGenres(
        genreScreen === undefined || !Array.isArray(genreScreen.genreList)
          ? []
          : genreScreen.genreList
      );

      Log.d(
        TAG,
        `loadShowsAndMoviesScreenNavigation() — synced ${response.screens.length} screens`
      );
      logShowsAndMoviesDiagnostic(
        "Shows & Movies navigation loaded",
        DiagnosticSeverity.Debug,
        {
          event: "navigation_load_succeeded",
          filter_count: String(filterOptions.length),
          genre_count: String(genreOptions.length)
        }
      );
      logShowsAndMoviesApiRequest(
        SHOWS_AND_MOVIES_SCREENS_API_ENDPOINT,
        "SUCCESS",
        startedAt
      );
      return { filterOptions, genreOptions };
    } catch (error: unknown) {
      if (isRequestCancellation(error, signal)) {
        Log.d(TAG, "loadShowsAndMoviesScreenNavigation() — cancelled");
        return rejectWithValue(REQUEST_CANCELLED);
      }
      Log.e(TAG, "loadShowsAndMoviesScreenNavigation()", error);
      logShowsAndMoviesDiagnostic(
        "Shows & Movies navigation failed",
        DiagnosticSeverity.Warning,
        {
          event: "navigation_load_failed",
          error_type: OperationalLogger.describeErrorType(error)
        }
      );
      logShowsAndMoviesApiRequest(
        SHOWS_AND_MOVIES_SCREENS_API_ENDPOINT,
        "FAIL",
        startedAt,
        error
      );
      return rejectWithValue(
        extractServiceErrorMessage(error, NAVIGATION_LOAD_FAILURE_MESSAGE)
      );
    }
  }
);

/**
 * Maps a single Showflix `Carousel` into app-ready carousel data.
 *
 * `resolvedCount` is preserved exactly as the shared mapper resolved it —
 * including `undefined` — rather than substituted with the current item
 * count. Collapsing an unknown total to "however many items just arrived"
 * would make an unfinished collection look already complete, so a rail whose
 * screen response omits the total would never page in a second batch.
 */
const mapShowflixCarouselToAppData = (
  carousel: ShowflixCarousel,
  imageHostBaseUrl: string
): TShowsAndMoviesCarouselData => {
  const page = mapShowflixCarouselItemsToAppData(carousel, imageHostBaseUrl);
  return {
    carouselId: carousel.carouselName,
    title: carousel.caption,
    layout: page.template,
    resolvedCount: page.resolvedCount,
    items: page.items
  };
};

const mapShowflixCarouselsToAppData = (
  response: ShowflixCarousels,
  imageHostBaseUrl: string
): TShowsAndMoviesCarouselData[] =>
  response.carousels === undefined || response.carousels === null
    ? []
    : response.carousels.map((carousel: ShowflixCarousel) =>
        mapShowflixCarouselToAppData(carousel, imageHostBaseUrl)
      );

const getRequestedGenres = (
  state: Pick<ShowsAndMoviesState, "selectedGenres" | "genreOptions">
): string[] =>
  state.selectedGenres
    .filter(
      (genreId: ShowsAndMoviesGenreId): boolean =>
        genreId !== ShowsAndMoviesGenreId.ALL
    )
    .map((genreId: ShowsAndMoviesGenreId): string => {
      const option: TShowsAndMoviesGenreMeta | undefined =
        state.genreOptions.find(
          (candidate: TShowsAndMoviesGenreMeta): boolean =>
            candidate.id === genreId
        );
      return option === undefined ? genreId : option.apiGenreId;
    });

const buildScreenParams = (
  state: ShowsAndMoviesState,
  pageCursor?: string
): TShowflixCarouselScreenGetParams => {
  const requestedGenres = getRequestedGenres(state);
  return {
    screenName: state.selectedFilter,
    genre: requestedGenres.length === 0 ? undefined : requestedGenres,
    initialFeedItemCount: 20,
    pageCursor
  };
};

/**
 * Fetches the rails for the active tab / genre combination.
 *
 * The tab and genre are read from state rather than passed in, so every caller
 * stays a bare `dispatch()` and cannot desync the request from the visible
 * strips. After fetching, syncs the active screen's genre list into the
 * API-provided genre options in Redux state.
 *
 * The real data path calls the Showflix `screenGet` endpoint with the tab as
 * the screenName and the genre as a query filter, then maps the response into
 * the carousel items.
 */
export const fetchShowsAndMoviesCarousels = createAsyncThunk<
  TShowsAndMoviesScreenPage,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "showsAndMovies/fetchCarousels",
  async (_: void, { getState, rejectWithValue, signal }) => {
    const startedAt: number = Date.now();
    let selectedFilter: ShowsAndMoviesFilterId | undefined;
    try {
      const state = getState().showsAndMovies;
      selectedFilter = state.selectedFilter;
      Log.i(
        TAG,
        `fetchShowsAndMoviesCarousels() — filter=${state.selectedFilter} genres=${state.selectedGenres.join(",")}`
      );

      const interactor = new PostSigninInteractor();
      const imageHostBaseUrl: string = await getImageHostBaseUrl(interactor);
      const response: ShowflixCarousels = await interactor.fetchShowflixScreen(
        buildScreenParams(state),
        { signal },
        getShowsAndMoviesFeatureArea(state.selectedFilter)
      );
      const carouselData = mapShowflixCarouselsToAppData(
        response,
        imageHostBaseUrl
      );

      Log.d(
        TAG,
        `fetchShowsAndMoviesCarousels() — fetched ${carouselData.length} carousels`
      );
      logShowsAndMoviesDiagnostic(
        "Shows & Movies carousels loaded",
        DiagnosticSeverity.Debug,
        {
          event: "carousels_load_succeeded",
          rail_count: String(carouselData.length),
          filter: state.selectedFilter
        }
      );
      logShowsAndMoviesApiRequest(
        SHOWS_AND_MOVIES_API_ENDPOINT,
        "SUCCESS",
        startedAt
      );
      return {
        items: carouselData,
        pageCursor: response.pageCursor || null,
        catalogueKey:
          state.selectedFilter + ":" + state.selectedGenres.join(","),
        refreshIntervalSecs:
          typeof response.refreshIntervalSecs === "number" &&
          Number.isFinite(response.refreshIntervalSecs) &&
          response.refreshIntervalSecs >= 0
            ? response.refreshIntervalSecs
            : DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS
      };
    } catch (error: unknown) {
      if (isRequestCancellation(error, signal)) {
        Log.d(TAG, "fetchShowsAndMoviesCarousels() — cancelled");
        return rejectWithValue(REQUEST_CANCELLED);
      }
      Log.e(TAG, "fetchShowsAndMoviesCarousels()", error);
      logShowsAndMoviesDiagnostic(
        "Shows & Movies carousels failed",
        DiagnosticSeverity.Warning,
        {
          event: "carousels_load_failed",
          error_type: OperationalLogger.describeErrorType(error),
          filter: selectedFilter !== undefined ? selectedFilter : "unknown"
        }
      );
      logShowsAndMoviesApiRequest(
        SHOWS_AND_MOVIES_API_ENDPOINT,
        "FAIL",
        startedAt,
        error
      );
      return rejectWithValue(
        extractServiceErrorMessage(error, CAROUSELS_LOAD_FAILURE_MESSAGE)
      );
    }
  }
);

export const refreshShowsAndMoviesCarousels =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const { selectedFilter, selectedGenres, carousels } =
      getState().showsAndMovies;
    if (carousels.isLoading || carousels.isLoadingMore) {
      return;
    }
    const catalogueKey = selectedFilter + ":" + selectedGenres.join(",");
    if (
      carousels.lastFetchTimestamp !== 0 &&
      carousels.lastFetchedCatalogueKey === catalogueKey &&
      Date.now() - carousels.lastFetchTimestamp <
        carousels.refreshIntervalSecs * 1000
    ) {
      return;
    }
    void dispatch(fetchShowsAndMoviesCarousels());
  };

export const loadMoreShowsAndMoviesCarousels = createAsyncThunk<
  TShowsAndMoviesScreenPage,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "showsAndMovies/loadMoreCarousels",
  async (_: void, { getState, rejectWithValue, signal }) => {
    const startedAt: number = Date.now();
    try {
      const state = getState().showsAndMovies;
      const interactor = new PostSigninInteractor();
      const imageHostBaseUrl = await getImageHostBaseUrl(interactor);
      const response = await interactor.fetchShowflixScreen(
        buildScreenParams(state, state.carousels.pageCursor || undefined),
        { signal },
        getShowsAndMoviesFeatureArea(state.selectedFilter)
      );
      logShowsAndMoviesApiRequest(
        SHOWS_AND_MOVIES_API_ENDPOINT,
        "SUCCESS",
        startedAt
      );
      return {
        items: mapShowflixCarouselsToAppData(response, imageHostBaseUrl),
        pageCursor: response.pageCursor || null
      };
    } catch (error: unknown) {
      Log.e(TAG, "loadMoreShowsAndMoviesCarousels()", error);
      logShowsAndMoviesDiagnostic(
        "Shows & Movies carousel pagination failed",
        DiagnosticSeverity.Debug,
        {
          event: "carousels_page_load_failed",
          error_type: OperationalLogger.describeErrorType(error)
        }
      );
      logShowsAndMoviesApiRequest(
        SHOWS_AND_MOVIES_API_ENDPOINT,
        "FAIL",
        startedAt,
        error
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to load more carousels"
      );
    }
  },
  {
    condition: (_: void, { getState }): boolean => {
      const carousels = getState().showsAndMovies.carousels;
      return (
        carousels.pageCursor !== null &&
        !carousels.isLoadingMore &&
        !carousels.isLoading
      );
    }
  }
);

export const loadMoreShowsAndMoviesCarouselItems = createAsyncThunk<
  TShowsAndMoviesCarouselPage,
  { carouselId: string },
  { state: UnityRootState; rejectValue: string }
>(
  "showsAndMovies/loadMoreCarouselItems",
  async ({ carouselId }, { getState, rejectWithValue, signal }) => {
    const startedAt: number = Date.now();
    try {
      const state = getState().showsAndMovies;
      const carousel = state.carousels.items.find(
        (item: TShowsAndMoviesCarouselData): boolean =>
          item.carouselId === carouselId
      );
      if (carousel === undefined) {
        return rejectWithValue("Carousel is no longer available");
      }
      const requestedCount: number = CAROUSEL_ITEM_PAGE_SIZE;
      const page = await fetchShowflixCarouselTilePage(
        carouselId,
        carousel.items.length,
        requestedCount,
        getRequestedGenres(state),
        ApplicationFeatureArea.SHOWS_AND_MOVIES,
        { signal }
      );
      let resolvedCount: number | undefined;

      if (page.resolvedCount !== undefined) {
        resolvedCount = page.resolvedCount;
      } else if (page.items.length < requestedCount) {
        resolvedCount = page.items.length;
      }

      logShowsAndMoviesApiRequest(
        "/v4/showsAndMovies/carousels/" + carouselId,
        "SUCCESS",
        startedAt
      );
      return {
        carouselId,
        items: page.items,
        resolvedCount,
        requestedCount
      };
    } catch (error: unknown) {
      logShowsAndMoviesDiagnostic(
        "Shows & Movies carousel items pagination failed",
        DiagnosticSeverity.Debug,
        {
          event: "carousel_items_page_load_failed",
          carousel_id: carouselId,
          error_type: OperationalLogger.describeErrorType(error)
        }
      );
      logShowsAndMoviesApiRequest(
        "/v4/showsAndMovies/carousels/" + carouselId,
        "FAIL",
        startedAt,
        error
      );
      OperationalLogger.logError({
        errorCode: "B302",
        errorMessage: OperationalLogger.describeErrorMessage(error)
      });
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to load more carousel items"
      );
    }
  },
  {
    condition: ({ carouselId }, { getState }): boolean => {
      const state = getState().showsAndMovies;
      const carousels = state.carousels;
      const carousel = carousels.items.find(
        (item: TShowsAndMoviesCarouselData): boolean =>
          item.carouselId === carouselId
      );
      return (
        !carousels.isLoading &&
        carousel !== undefined &&
        (carousel.resolvedCount === undefined ||
          carousel.items.length < carousel.resolvedCount) &&
        (isSingleCarouselGrid(state) ||
          carousel.items.length < SEE_MORE_RAIL_TILE_CAP) &&
        !carousels.loadingCarouselIds.includes(carouselId)
      );
    }
  }
);

// ── Initial state ─────────────────────────────────────────────────────────────

const INITIAL_STATE: ShowsAndMoviesState = {
  selectedFilter: ShowsAndMoviesFilterId.FOR_YOU,
  selectedGenres: [ShowsAndMoviesGenreId.ALL],
  filterOptions: [],
  genreOptions: [],
  carousels: {
    items: [],
    pageCursor: null,
    refreshIntervalSecs: DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS,
    lastFetchTimestamp: 0,
    lastFetchedCatalogueKey: "",
    isLoading: false,
    isLoadingMore: false,
    loadingCarouselIds: [],
    error: "",
    requestId: null
  },
  focusRegion: ShowsAndMoviesFocusRegion.FILTERS,
  focusedFilterIndex: 0,
  focusedGenreIndex: 0,
  focusedCarouselIndex: 0,
  focusedItemIndex: 0,
  carouselScrollPositions: [],
  carouselItemCounts: [],
  navigationError: ""
};

// ── Slice ─────────────────────────────────────────────────────────────────────

const showsAndMoviesSlice = createSlice({
  name: "showsAndMovies",
  initialState: INITIAL_STATE,
  reducers: {
    // ── Focus setters (pointer / hover path) ──

    /** Applies the tab at `filterIndex` and resets the genre and rail cursors. */
    focusShowsAndMoviesFilter(
      state: ShowsAndMoviesState,
      action: PayloadAction<{
        filterIndex: number;
        filterId: ShowsAndMoviesFilterId;
      }>
    ): void {
      state.focusRegion = ShowsAndMoviesFocusRegion.FILTERS;
      state.focusedFilterIndex = action.payload.filterIndex;
      if (state.selectedFilter === action.payload.filterId) {
        return;
      }
      state.selectedFilter = action.payload.filterId;
      // A new catalogue starts unfiltered: the previous tab's genre says
      // nothing about this one's rails.
      state.selectedGenres = [ShowsAndMoviesGenreId.ALL];
      state.focusedGenreIndex = 0;
      clearCarouselsForFilterChange(state);
    },

    /** Applies the genre chip at `genreIndex` and resets the rail cursor. */
    focusShowsAndMoviesGenre(
      state: ShowsAndMoviesState,
      action: PayloadAction<{
        genreIndex: number;
        genreId: ShowsAndMoviesGenreId;
      }>
    ): void {
      state.focusRegion = ShowsAndMoviesFocusRegion.GENRES;
      state.focusedGenreIndex = action.payload.genreIndex;
      state.selectedGenres = toggleGenreSelection(
        state.selectedGenres,
        action.payload.genreId
      );
      resetCarouselFocus(state);
    },

    /** Moves focus to a specific tile in a specific carousel row. */
    focusShowsAndMoviesTile(
      state: ShowsAndMoviesState,
      action: PayloadAction<{ carouselIndex: number; itemIndex: number }>
    ): void {
      state.focusRegion = ShowsAndMoviesFocusRegion.CAROUSEL_ITEMS;
      state.focusedCarouselIndex = action.payload.carouselIndex;
      state.focusedItemIndex = action.payload.itemIndex;
    },

    // ── D-pad commit actions (OK on a focused chip) ──

    /** Commits the highlighted tab as the active one. */
    applyShowsAndMoviesFilter(state: ShowsAndMoviesState): void {
      const filter: TShowsAndMoviesFilterMeta | undefined =
        state.filterOptions[state.focusedFilterIndex];
      if (filter === undefined) {
        return;
      }
      if (state.selectedFilter === filter.id) {
        return;
      }
      state.selectedFilter = filter.id;
      state.selectedGenres = [ShowsAndMoviesGenreId.ALL];
      state.focusedGenreIndex = 0;
      clearCarouselsForFilterChange(state);
      Log.d(TAG, `applyShowsAndMoviesFilter() — filter=${filter.id}`);
    },

    /** Commits the highlighted genre chip as the active one. */
    applyShowsAndMoviesGenre(state: ShowsAndMoviesState): void {
      const genre: TShowsAndMoviesGenreMeta | undefined =
        state.genreOptions[state.focusedGenreIndex];
      if (genre === undefined) {
        return;
      }
      state.selectedGenres = toggleGenreSelection(
        state.selectedGenres,
        genre.id
      );
      resetCarouselFocus(state);
      Log.d(
        TAG,
        `applyShowsAndMoviesGenre() — genres=${state.selectedGenres.join(",")}`
      );
    },

    // ── View → state sync ──

    /**
     * Syncs the focusable slot count per visible row so the navigation reducers
     * can compute bounds from state alone. Dispatch from a `useEffect` in the
     * view whenever the visible rails change.
     *
     * Slots, not items: a row that ends in a See More tile has one more focus
     * stop than it has tiles.
     *
     * TODO(UAW): Remove once the rails are held directly in this slice.
     */
    setShowsAndMoviesCarouselItemCounts(
      state: ShowsAndMoviesState,
      action: PayloadAction<number[]>
    ): void {
      state.carouselItemCounts = action.payload;
      state.carouselScrollPositions = action.payload.map(
        (count: number, index: number): number => {
          const saved: number | undefined =
            state.carouselScrollPositions[index];
          return clampIndex(saved !== undefined ? saved : 0, count);
        }
      );
      state.focusedCarouselIndex = clampIndex(
        state.focusedCarouselIndex,
        action.payload.length
      );
      state.focusedItemIndex = clampIndex(
        state.focusedItemIndex,
        action.payload[state.focusedCarouselIndex] || 0
      );
      if (
        action.payload.length === 0 &&
        state.focusRegion === ShowsAndMoviesFocusRegion.CAROUSEL_ITEMS
      ) {
        returnFocusToFilters(state);
      }
    },

    // ── D-pad navigation ──

    /** Moves focus left within the active region. */
    navigateShowsAndMoviesLeft(state: ShowsAndMoviesState): void {
      if (state.focusRegion === ShowsAndMoviesFocusRegion.FILTERS) {
        state.focusedFilterIndex = clampIndex(
          state.focusedFilterIndex - 1,
          state.filterOptions.length
        );
        return;
      }
      if (state.focusRegion === ShowsAndMoviesFocusRegion.GENRES) {
        state.focusedGenreIndex = clampIndex(
          state.focusedGenreIndex - 1,
          state.genreOptions.length
        );
        return;
      }
      if (isSingleCarouselGrid(state)) {
        const next = moveGridFocus(state.focusedItemIndex, GridDirection.LEFT, {
          itemCount: state.carouselItemCounts[0] || 0,
          columns: SINGLE_CAROUSEL_GRID_COLUMNS,
          horizontalEdge: GridEdgeBehaviour.CLAMP,
          verticalEdge: GridEdgeBehaviour.EXIT
        });
        if (next !== null) {
          state.focusedItemIndex = next;
        }
        return;
      }
      const next: number | null = moveCarouselTileLeft(state.focusedItemIndex);
      if (next !== null) {
        state.focusedItemIndex = next;
      }
    },

    /** Moves focus right within the active region. */
    navigateShowsAndMoviesRight(state: ShowsAndMoviesState): void {
      if (state.focusRegion === ShowsAndMoviesFocusRegion.FILTERS) {
        state.focusedFilterIndex = clampIndex(
          state.focusedFilterIndex + 1,
          state.filterOptions.length
        );
        return;
      }
      if (state.focusRegion === ShowsAndMoviesFocusRegion.GENRES) {
        state.focusedGenreIndex = clampIndex(
          state.focusedGenreIndex + 1,
          state.genreOptions.length
        );
        return;
      }
      if (isSingleCarouselGrid(state)) {
        const next = moveGridFocus(
          state.focusedItemIndex,
          GridDirection.RIGHT,
          {
            itemCount: state.carouselItemCounts[0] || 0,
            columns: SINGLE_CAROUSEL_GRID_COLUMNS,
            horizontalEdge: GridEdgeBehaviour.CLAMP,
            verticalEdge: GridEdgeBehaviour.EXIT
          }
        );
        if (next !== null) {
          state.focusedItemIndex = next;
        }
        return;
      }
      const rawCount: number | undefined =
        state.carouselItemCounts[state.focusedCarouselIndex];
      const next: number | null = moveCarouselTileRight(
        state.focusedItemIndex,
        rawCount !== undefined ? rawCount : 0
      );
      if (next !== null) {
        state.focusedItemIndex = next;
      }
    },

    /** Moves focus up across regions, or to the previous carousel row. */
    navigateShowsAndMoviesUp(state: ShowsAndMoviesState): void {
      if (state.focusRegion === ShowsAndMoviesFocusRegion.GENRES) {
        returnFocusToFilters(state);
        return;
      }
      if (state.focusRegion !== ShowsAndMoviesFocusRegion.CAROUSEL_ITEMS) {
        // The tab strip is the top of the page — nothing above it.
        return;
      }
      if (isSingleCarouselGrid(state)) {
        const next = moveGridFocus(state.focusedItemIndex, GridDirection.UP, {
          itemCount: state.carouselItemCounts[0] || 0,
          columns: SINGLE_CAROUSEL_GRID_COLUMNS,
          horizontalEdge: GridEdgeBehaviour.CLAMP,
          verticalEdge: GridEdgeBehaviour.EXIT
        });
        if (next !== null) {
          state.focusedItemIndex = next;
          return;
        }
        enterRegionAboveCarousels(state);
        return;
      }
      if (state.focusedCarouselIndex === 0) {
        saveFocusedCarouselPosition(state);
        enterRegionAboveCarousels(state);
        return;
      }
      const move = moveCarouselRowUp(
        state.focusedCarouselIndex,
        state.focusedItemIndex,
        [...state.carouselScrollPositions],
        state.carouselItemCounts
      );
      if (move !== null) {
        state.focusedCarouselIndex = move.carouselIndex;
        state.focusedItemIndex = move.itemIndex;
        state.carouselScrollPositions = move.savedScrollPositions;
      }
    },

    /** Moves focus down across regions, or to the next carousel row. */
    navigateShowsAndMoviesDown(state: ShowsAndMoviesState): void {
      if (state.focusRegion === ShowsAndMoviesFocusRegion.FILTERS) {
        // The genre chips sit between the tabs and the rails, so they take
        // focus first on the tabs that have them.
        if (hasGenreSubFilters(state.selectedFilter)) {
          state.focusRegion = ShowsAndMoviesFocusRegion.GENRES;
          return;
        }
        moveToFirstCarousel(state);
        return;
      }
      if (state.focusRegion === ShowsAndMoviesFocusRegion.GENRES) {
        moveToFirstCarousel(state);
        return;
      }
      if (isSingleCarouselGrid(state)) {
        const next = moveGridFocus(state.focusedItemIndex, GridDirection.DOWN, {
          itemCount: state.carouselItemCounts[0] || 0,
          columns: SINGLE_CAROUSEL_GRID_COLUMNS,
          horizontalEdge: GridEdgeBehaviour.CLAMP,
          verticalEdge: GridEdgeBehaviour.CLAMP
        });
        if (next !== null) {
          state.focusedItemIndex = next;
        }
        return;
      }
      const move = moveCarouselRowDown(
        state.focusedCarouselIndex,
        state.focusedItemIndex,
        [...state.carouselScrollPositions],
        state.carouselItemCounts
      );
      if (move !== null) {
        state.focusedCarouselIndex = move.carouselIndex;
        state.focusedItemIndex = move.itemIndex;
        state.carouselScrollPositions = move.savedScrollPositions;
      }
    },

    resetShowsAndMoviesState(state: ShowsAndMoviesState): ShowsAndMoviesState {
      return {
        ...INITIAL_STATE,
        filterOptions: state.filterOptions,
        genreOptions: state.genreOptions
      };
    }
  },
  extraReducers: (builder): void => {
    builder
      .addCase(
        loadShowsAndMoviesScreenNavigation.pending,
        (state: ShowsAndMoviesState): void => {
          state.navigationError = "";
        }
      )
      .addCase(
        loadShowsAndMoviesScreenNavigation.fulfilled,
        (
          state: ShowsAndMoviesState,
          action: PayloadAction<
            Pick<ShowsAndMoviesState, "filterOptions" | "genreOptions">
          >
        ): void => {
          if (action.payload.filterOptions.length === 0) {
            state.navigationError =
              state.filterOptions.length === 0
                ? NAVIGATION_LOAD_FAILURE_MESSAGE
                : "";
            return;
          }
          state.filterOptions = action.payload.filterOptions;
          state.genreOptions = action.payload.genreOptions;
          state.navigationError = "";
          // default tab, falling back to the first supported tab. Preserve
          // the current selection when cached carousels mean this is a
          // backward-sticky return.
          if (
            state.carousels.items.length === 0 &&
            action.payload.filterOptions.length > 0
          ) {
            const defaultFilterCandidate:
              | TShowsAndMoviesFilterMeta
              | undefined = action.payload.filterOptions.find(
              (option: TShowsAndMoviesFilterMeta): boolean =>
                option.isDefault === true
            );
            const defaultFilter: TShowsAndMoviesFilterMeta =
              defaultFilterCandidate === undefined
                ? action.payload.filterOptions[0]
                : defaultFilterCandidate;
            state.selectedFilter = defaultFilter.id;
            state.selectedGenres = [ShowsAndMoviesGenreId.ALL];
            state.focusedFilterIndex = action.payload.filterOptions.findIndex(
              (option: TShowsAndMoviesFilterMeta): boolean =>
                option.id === defaultFilter.id
            );
            resetCarouselFocus(state);
          }
        }
      )
      .addCase(
        loadShowsAndMoviesScreenNavigation.rejected,
        (state: ShowsAndMoviesState, action): void => {
          if (state.filterOptions.length > 0) {
            state.navigationError = "";
            return;
          }
          state.navigationError =
            action.payload !== undefined
              ? action.payload
              : NAVIGATION_LOAD_FAILURE_MESSAGE;
        }
      )
      .addCase(
        fetchShowsAndMoviesCarousels.pending,
        (state: ShowsAndMoviesState, action): void => {
          state.carousels.requestId =
            action && action.meta && action.meta.requestId != null
              ? action.meta.requestId
              : null;
          // Clear the page cursor when a full refresh starts so a previous
          // catalogue cursor cannot be mixed into the new filter/genre request.
          state.carousels.pageCursor = null;
          state.carousels.isLoading = true;
          state.carousels.error = "";
        }
      )
      .addCase(
        fetchShowsAndMoviesCarousels.fulfilled,
        (
          state: ShowsAndMoviesState,
          action: TGuardedAction<TShowsAndMoviesScreenPage>
        ): void => {
          // A stale response from a filter/genre the user has since changed —
          // ignore it so it cannot overwrite the newer selection's rails.
          const actionRequestId =
            action && action.meta && action.meta.requestId != null
              ? action.meta.requestId
              : null;
          if (actionRequestId !== state.carousels.requestId) {
            return;
          }
          state.carousels.items = action.payload.items;
          state.carousels.pageCursor = action.payload.pageCursor;
          state.carousels.refreshIntervalSecs =
            action.payload.refreshIntervalSecs === undefined
              ? DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS
              : action.payload.refreshIntervalSecs;
          state.carousels.lastFetchTimestamp = Date.now();
          state.carousels.lastFetchedCatalogueKey =
            action.payload.catalogueKey === undefined
              ? state.selectedFilter + ":" + state.selectedGenres.join(",")
              : action.payload.catalogueKey;
          state.carousels.isLoading = false;
        }
      )
      .addCase(
        fetchShowsAndMoviesCarousels.rejected,
        (state: ShowsAndMoviesState, action): void => {
          const actionRequestId =
            action && action.meta && action.meta.requestId != null
              ? action.meta.requestId
              : null;
          if (actionRequestId !== state.carousels.requestId) {
            return;
          }
          state.carousels.isLoading = false;
          state.carousels.error =
            action.payload !== undefined
              ? action.payload
              : "Failed to fetch Shows & Movies carousels";
        }
      )
      .addCase(
        loadMoreShowsAndMoviesCarousels.pending,
        (state, action): void => {
          state.carousels.requestId =
            action && action.meta && action.meta.requestId != null
              ? action.meta.requestId
              : null;
          state.carousels.isLoadingMore = true;
        }
      )
      .addCase(
        loadMoreShowsAndMoviesCarousels.fulfilled,
        (state, action: TGuardedAction<TShowsAndMoviesScreenPage>): void => {
          const actionRequestId =
            action && action.meta && action.meta.requestId != null
              ? action.meta.requestId
              : null;
          if (actionRequestId !== state.carousels.requestId) {
            return;
          }
          const existingIds = new Set(
            state.carousels.items.map((item) => item.carouselId)
          );
          state.carousels.items.push(
            ...action.payload.items.filter(
              (item) => !existingIds.has(item.carouselId)
            )
          );
          state.carousels.pageCursor = action.payload.pageCursor;
          state.carousels.isLoadingMore = false;
        }
      )
      .addCase(
        loadMoreShowsAndMoviesCarousels.rejected,
        (state, action): void => {
          const actionRequestId =
            action && action.meta && action.meta.requestId != null
              ? action.meta.requestId
              : null;
          if (actionRequestId !== state.carousels.requestId) {
            return;
          }
          state.carousels.isLoadingMore = false;
        }
      )
      .addCase(
        loadMoreShowsAndMoviesCarouselItems.pending,
        (state, action): void => {
          state.carousels.loadingCarouselIds.push(action.meta.arg.carouselId);
        }
      )
      .addCase(
        loadMoreShowsAndMoviesCarouselItems.fulfilled,
        (state, action): void => {
          const carousel = state.carousels.items.find(
            (item) => item.carouselId === action.payload.carouselId
          );
          if (carousel !== undefined) {
            const existingIds = new Set(carousel.items.map((item) => item.id));
            carousel.items.push(
              ...action.payload.items.filter(
                (item) => !existingIds.has(item.id)
              )
            );
            if (action.payload.resolvedCount !== undefined) {
              carousel.resolvedCount = action.payload.resolvedCount;
            } else if (
              action.payload.items.length < action.payload.requestedCount
            ) {
              // Server omitted a total but returned a short page — that is the
              // signal the collection is exhausted.
              carousel.resolvedCount = carousel.items.length;
            }
          }
          state.carousels.loadingCarouselIds =
            state.carousels.loadingCarouselIds.filter(
              (id) => id !== action.payload.carouselId
            );
        }
      )
      .addCase(
        loadMoreShowsAndMoviesCarouselItems.rejected,
        (state, action): void => {
          state.carousels.loadingCarouselIds =
            state.carousels.loadingCarouselIds.filter(
              (id) => id !== action.meta.arg.carouselId
            );
        }
      );
  }
});

// ── Exported actions ──────────────────────────────────────────────────────────

export const {
  focusShowsAndMoviesFilter,
  focusShowsAndMoviesGenre,
  focusShowsAndMoviesTile,
  applyShowsAndMoviesFilter,
  applyShowsAndMoviesGenre,
  setShowsAndMoviesCarouselItemCounts,
  navigateShowsAndMoviesLeft,
  navigateShowsAndMoviesRight,
  navigateShowsAndMoviesUp,
  navigateShowsAndMoviesDown
} = showsAndMoviesSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectShowsAndMoviesSelectedFilter = (
  state: UnityRootState
): ShowsAndMoviesFilterId => state.showsAndMovies.selectedFilter;

export const selectShowsAndMoviesSelectedGenres = (
  state: UnityRootState
): ShowsAndMoviesGenreId[] => state.showsAndMovies.selectedGenres;

/**
 * Memoized on the two fields it reads, not the whole slice: keyed on the slice,
 * every unrelated write (a loading flag, an error) returned a new array, which
 * rebuilt the screen controller through the callbacks that depend on it — and
 * a rebuild re-registers the controller as the active screen, even mid-exit.
 */
export const selectShowsAndMoviesSelectedApiGenres = createSelector(
  [
    (state: UnityRootState): ShowsAndMoviesGenreId[] =>
      state.showsAndMovies.selectedGenres,
    (state: UnityRootState): TShowsAndMoviesGenreMeta[] =>
      state.showsAndMovies.genreOptions
  ],
  (
    selectedGenres: ShowsAndMoviesGenreId[],
    genreOptions: TShowsAndMoviesGenreMeta[]
  ): string[] => getRequestedGenres({ selectedGenres, genreOptions })
);

export const selectShowsAndMoviesScreenTemplateType = (
  state: UnityRootState
): TShowsAndMoviesFilterMeta["templateType"] =>
  getSelectedScreenTemplateType(state.showsAndMovies);

export const selectShowsAndMoviesFilterOptions = (
  state: UnityRootState
): TShowsAndMoviesFilterMeta[] => state.showsAndMovies.filterOptions;

export const selectShowsAndMoviesGenreOptions = (
  state: UnityRootState
): TShowsAndMoviesGenreMeta[] => state.showsAndMovies.genreOptions;

export const selectShowsAndMoviesCarousels = (
  state: UnityRootState
): TShowsAndMoviesCarouselData[] => state.showsAndMovies.carousels.items;

export const selectIsLoadingShowsAndMoviesCarousels = (
  state: UnityRootState
): boolean => state.showsAndMovies.carousels.isLoading;

export const selectShowsAndMoviesLoadingCarouselIds = (
  state: UnityRootState
): string[] => state.showsAndMovies.carousels.loadingCarouselIds;

export const selectShowsAndMoviesHasMoreCarousels = (
  state: UnityRootState
): boolean => state.showsAndMovies.carousels.pageCursor !== null;

export const selectShowsAndMoviesCarouselsError = (
  state: UnityRootState
): string => state.showsAndMovies.carousels.error;

export const selectShowsAndMoviesNavigationError = (
  state: UnityRootState
): string => state.showsAndMovies.navigationError;

export const selectShowsAndMoviesFocusRegion = (
  state: UnityRootState
): ShowsAndMoviesFocusRegion => state.showsAndMovies.focusRegion;

export const selectShowsAndMoviesFocusedFilterIndex = (
  state: UnityRootState
): number => state.showsAndMovies.focusedFilterIndex;

export const selectShowsAndMoviesFocusedGenreIndex = (
  state: UnityRootState
): number => state.showsAndMovies.focusedGenreIndex;

export const selectShowsAndMoviesFocusedCarouselIndex = (
  state: UnityRootState
): number => state.showsAndMovies.focusedCarouselIndex;

export const selectShowsAndMoviesFocusedItemIndex = (
  state: UnityRootState
): number => state.showsAndMovies.focusedItemIndex;

export const selectShowsAndMoviesCarouselScrollPositions = (
  state: UnityRootState
): number[] => state.showsAndMovies.carouselScrollPositions;

export default showsAndMoviesSlice.reducer;
